import { configureStore } from '@reduxjs/toolkit';
import { useDispatch, TypedUseSelectorHook, useSelector } from 'react-redux';
import { alertSlice } from '../../context/AlertContext';
import { authenticationSettingsSlice } from '../../context/AuthenticationSettingsContext';
import { binStatusSlice } from '../../context/BinStatusContext';
import { commentSlice } from '../../context/CommentContext';
import { containerTypeSlice } from '../../context/ContainerTypeContext';
import { csvUploadConfigurationSlice } from '../../context/CsvUploadConfigurationContext';
import { customFieldSlice } from '../../context/CustomFieldContext';
import { fileSlice } from '../../context/FileContext';
import { orderExportConfigurationSlice } from '../../context/OrderExportConfigurationContext';
import { reorderRuleSlice } from '../../context/ReorderRuleContext';
import { reorderUserRuleSlice } from '../../context/ReorderUserRuleContext';
import { scanRuleSlice } from '../../context/ScanRuleContext';
import { stockLocationRoleAssignmentSlice } from '../../context/StockLocationRoleAssignmentContext';
import { tagSlice } from '../../context/TagContext';
import { tagRelationSlice } from '../../context/TagRelationContext';
import { taskSlice } from '../../context/TaskContext';
import { taskExportConfigurationSlice } from '../../context/TaskExportConfigurationContext';
import { taskSettingsSlice } from '../../context/TaskSettingsContext';
import { productMasterDataSlice } from '../../providers/ProductMasterDataProvider';
import { productStatusSlice } from '../../providers/ProductStatusProvider';
import { productTransactionStatusSlice } from '../../providers/ProductTransactionStatusProvider';
import { orderStatusSlice } from '../../providers/OrderStatusProvider';
import { orderFlowSlice } from '../../providers/OrderFlowProvider';
import { orderSlice } from '../../providers/OrderProvider';
import { toastSlice } from '../../context/ToastContext';
import { productSlice } from '../../providers/ProductProvider';
import { triggerSlice } from '../../providers/TriggerProvider';
import { productTransactionSlice } from '../../providers/ProductTransactionProvider';
import { reorderRuleExportConfigurationSlice } from '../../providers/ReorderRuleExportConfigurationProvider';
import { handlingUnitSlice } from '../../providers/HandlingUnitProvider';
import { kitTemplateSlice } from '../../context/KitTemplateContext';
import { kitSlice } from '../../context/KitContext';
import { integrationSettingsSlice } from '../../providers/IntegrationProvider';
import { binSlice } from '../../providers/BinProvider';
import { lotSlice } from '../../providers/LotProvider';
import { containerSlice } from '../../providers/ContainerProvider';
import { stockLocationSlice } from '../../providers/StockLocationProvider';
import { productExportConfigurationSlice } from '../../providers/ProductExportConfigurationProvider';
import { contactSlice } from '../../providers/ContactProvider';
import { productMasterDataExportConfigurationSlice } from '../../providers/ProductMasterDataExportConfigurationProvider';
import { barcodeExportConfigurationSlice } from '../../providers/BarcodeExportConfigurationProvider';
import { dashboardSlice } from '../../providers/DashboardProvider';
import { binConstraintSlice } from '../../providers/BinConstraintProvider';
import { assetTransactionSlice } from '../../providers/AssetTransactionProvider';
import { assetSlice } from '../../providers/AssetProvider';
import { pdfTemplateSlice } from '../../providers/PdfTemplateProvider';
import { tableSettingsSlice } from '../../providers/TableSettingsProvider';
import { productTransactionExportConfigurationSlice } from '../../providers/ProductTransactionExportConfigurationProvider';
import { assetExportConfigurationSlice } from '../../providers/AssetExportConfigurationProvider';

export const store = configureStore({
  middleware: getDefaultMiddleware => getDefaultMiddleware({ serializableCheck: false }),
  reducer: {
    alerts: alertSlice.reducer,
    assets: assetSlice.reducer,
    assetExportConfigurations: assetExportConfigurationSlice.reducer,
    assetTransactions: assetTransactionSlice.reducer,
    authenticationSettings: authenticationSettingsSlice.reducer,
    barcodeExportConfigurations: barcodeExportConfigurationSlice.reducer,
    bins: binSlice.reducer,
    binContraints: binConstraintSlice.reducer,
    binStatuses: binStatusSlice.reducer,
    comments: commentSlice.reducer,
    contacts: contactSlice.reducer,
    containers: containerSlice.reducer,
    containerTypes: containerTypeSlice.reducer,
    csvUploadConfigurations: csvUploadConfigurationSlice.reducer,
    customFields: customFieldSlice.reducer,
    dashboard: dashboardSlice.reducer,
    files: fileSlice.reducer,
    handlingUnits: handlingUnitSlice.reducer,
    integrationSettings: integrationSettingsSlice.reducer,
    kits: kitSlice.reducer,
    kitTemplates: kitTemplateSlice.reducer,
    lots: lotSlice.reducer,
    orderExportConfigurations: orderExportConfigurationSlice.reducer,
    orderFlows: orderFlowSlice.reducer,
    orderStatuses: orderStatusSlice.reducer,
    orders: orderSlice.reducer,
    pdfTemplates: pdfTemplateSlice.reducer,
    productExportConfigurations: productExportConfigurationSlice.reducer,
    productMasterData: productMasterDataSlice.reducer,
    productMasterDataExportConfigurations: productMasterDataExportConfigurationSlice.reducer,
    productStatuses: productStatusSlice.reducer,
    products: productSlice.reducer,
    productTransactionExportConfigurations: productTransactionExportConfigurationSlice.reducer,
    productTransactionStatuses: productTransactionStatusSlice.reducer,
    productTransactions: productTransactionSlice.reducer,
    reorderRuleExportConfigurations: reorderRuleExportConfigurationSlice.reducer,
    reorderRules: reorderRuleSlice.reducer,
    reorderUserRules: reorderUserRuleSlice.reducer,
    scanRules: scanRuleSlice.reducer,
    stockLocationRoleAssignments: stockLocationRoleAssignmentSlice.reducer,
    stockLocations: stockLocationSlice.reducer,
    tableSettings: tableSettingsSlice.reducer,
    tagRelations: tagRelationSlice.reducer,
    tags: tagSlice.reducer,
    taskExportConfigurations: taskExportConfigurationSlice.reducer,
    tasks: taskSlice.reducer,
    taskSettings: taskSettingsSlice.reducer,
    toasts: toastSlice.reducer,
    triggers: triggerSlice.reducer,
  },
});

type RootState = ReturnType<typeof store.getState>;
type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
